import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Label,
  Spinner,
} from 'reactstrap';
import { useOrganizationStore } from '../../store/organization.store';
import { useNotificationsStore } from '../../store/notifications.store';
import { filterByEnabledSince } from '../../services/notificationFilter';
import WavePlayer from '../WavePlayer';

interface PendingReview {
  id: string;
  trackName: string;
  sourceOrgId: string;
  sourceOrgName: string;
  sourceTrackId: string;
  reviewId: string;
  mp3Files?: { downloadUrl: string; storageRef: string; fileName: string }[];
  wavFiles?: { downloadUrl: string; storageRef: string; fileName: string }[];
  defaultMp3Id?: string;
  language?: { label: string; value: string }[];
  client?: { label: string; value: string };
  agency?: { label: string; value: string };
  tags?: { label: string; value: string }[];
  sharedAt?: number;
  /** Spot description from audiometa.notes — surfaced so reviewers can read
   *  the brief and confirm the audio matches. Populated when the track was
   *  matched to a chase-list station with a description. */
  notes?: string;
  /** Snapshot of stations this spot is flighting on. Populated only when
   *  the review was generated from a campaign (batch path). Library-
   *  originated reviews carry this as undefined / empty. */
  stations?: Array<{ station: string; mediaOwner: string; flightingCode: string }>;
}

interface PendingReviewsModalProps {
  firebase: any;
}

const PendingReviewsModal: React.FC<PendingReviewsModalProps> = ({ firebase }) => {
  const [pendingReviews, setPendingReviews] = useState<PendingReview[]>([]);
  const [loading, setLoading] = useState(false);
  const [submittingIds, setSubmittingIds] = useState<Set<string>>(new Set());
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const [activeReviewId, setActiveReviewId] = useState<string | null>(null);
  const [hasStartedPlayback, setHasStartedPlayback] = useState(false);

  const { currentOrgId, currentOrgName } = useOrganizationStore();

  const isOpen = useNotificationsStore(state => state.showPendingReviewsModal);
  const setIsOpen = useNotificationsStore(state => state.setShowPendingReviewsModal);
  const setPendingReviewsCount = useNotificationsStore(state => state.setPendingReviewsCount);

  // Reset auto-open flag when org changes
  useEffect(() => {
    setHasAutoOpened(false);
  }, [currentOrgId]);

  // Real-time listener for pending reviews
  useEffect(() => {
    if (!currentOrgId || !firebase?.subscribePendingReviewsForOrg) {
      setPendingReviews([]);
      setPendingReviewsCount(0);
      return;
    }

    setLoading(true);
    const unsubscribe = firebase.subscribePendingReviewsForOrg(
      currentOrgId,
      async (reviews: PendingReview[]) => {
        // Defensive sync: an incomingReviews entry should always correspond
        // to a still-pending review record on the source. If the review was
        // resolved/recalled elsewhere (race) and the deletion didn't fire,
        // drop the stale bell entry here.
        const stillPending: PendingReview[] = [];
        for (const review of reviews) {
          if (review.sourceOrgId && review.sourceTrackId && review.reviewId) {
            try {
              const reviewRecords = await firebase.getReviewsForTrack(review.sourceOrgId, review.sourceTrackId);
              const matchingReview = reviewRecords.find((r: any) => r.id === review.reviewId);
              if (matchingReview && matchingReview.status !== 'pending') {
                // Already resolved on the source — clean up the orphan
                // bell entry. Doc id === reviewId in the new schema.
                try {
                  const { doc: fbDoc, deleteDoc: fbDelete } = await import('firebase/firestore');
                  const { getOrgCollectionPath } = await import('../../constants/paths');
                  await fbDelete(fbDoc(firebase.db, getOrgCollectionPath(currentOrgId, 'incomingReviews'), review.id));
                } catch { /* best effort */ }
                continue; // Don't add to pending list
              }
            } catch {
              // If we can't check, keep it as pending
            }
          }
          stillPending.push(review);
        }

        const enabledSince = useNotificationsStore.getState().inAppEnabledSince;
        const filtered = filterByEnabledSince('reviewNotifications', stillPending, enabledSince, (n) => n.sharedAt);
        setPendingReviews(filtered);
        setPendingReviewsCount(filtered.length);
        setLoading(false);

        // Auto-open on first load if there are pending reviews
        if (!hasAutoOpened && filtered.length > 0) {
          setIsOpen(true);
          setHasAutoOpened(true);
        }
      }
    );

    return () => {
      if (unsubscribe) unsubscribe();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrgId, firebase]);

  // Auto-select first review when list changes or modal opens
  useEffect(() => {
    if (isOpen && pendingReviews.length > 0 && !activeReviewId) {
      setActiveReviewId(pendingReviews[0].id);
    }
    // Clear active if list becomes empty
    if (pendingReviews.length === 0) {
      setActiveReviewId(null);
    }
  }, [isOpen, pendingReviews, activeReviewId]);

  // Reset when modal closes
  useEffect(() => {
    if (!isOpen) {
      setActiveReviewId(null);
      setHasStartedPlayback(false);
    }
  }, [isOpen]);

  // Keyboard navigation within the modal
  useEffect(() => {
    if (!isOpen || pendingReviews.length === 0) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      // Skip if typing in an input
      if (document.activeElement?.tagName === 'INPUT' || document.activeElement?.tagName === 'TEXTAREA') {
        return;
      }

      const currentIndex = pendingReviews.findIndex(r => r.id === activeReviewId);

      switch (e.key) {
        case ' ':
          // Track that user has started playback — enables autoplay on navigation
          setHasStartedPlayback(true);
          break;
        case 'ArrowDown':
          e.preventDefault();
          if (currentIndex < pendingReviews.length - 1) {
            setActiveReviewId(pendingReviews[currentIndex + 1].id);
          }
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (currentIndex > 0) {
            setActiveReviewId(pendingReviews[currentIndex - 1].id);
          }
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, pendingReviews, activeReviewId]);

  // Resolve a playback URL for a pending review. Prefers the default MP3,
  // then any MP3, then falls back to the first WAV when no MP3 is attached.
  // WAV is natively playable in modern browsers via HTML5 audio. Without
  // this fallback, WAV-only spots showed no play option in the bell modal.
  const getPlaybackUrl = (review: PendingReview): string | null => {
    const { mp3Files, wavFiles, defaultMp3Id } = review;
    if (mp3Files && mp3Files.length > 0) {
      if (defaultMp3Id) {
        const defaultFile = mp3Files.find(f => f.storageRef === defaultMp3Id);
        if (defaultFile?.downloadUrl) return defaultFile.downloadUrl;
      }
      const firstMp3 = mp3Files[0]?.downloadUrl;
      if (firstMp3) return firstMp3;
    }
    if (wavFiles && wavFiles.length > 0) {
      return wavFiles[0]?.downloadUrl || null;
    }
    return null;
  };

  const handleApprove = async (review: PendingReview) => {
    if (!firebase) return;

    setSubmittingIds(prev => new Set(prev).add(review.id));
    try {
      const success = await firebase.submitReview(
        review.sourceOrgId,
        review.sourceTrackId,
        review.reviewId,
        'approved',
        {
          reviewerName: firebase.auth.currentUser?.displayName || firebase.auth.currentUser?.email || currentOrgName,
          reviewerEmail: firebase.auth.currentUser?.email || null,
          reviewedVia: 'in-app',
        }
      );

      if (success) {
        setPendingReviews(prev => {
          const updated = prev.filter(r => r.id !== review.id);
          setPendingReviewsCount(updated.length);
          return updated;
        });
      }
    } catch (error) {
      console.error('Error approving review:', error);
    } finally {
      setSubmittingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(review.id);
        return newSet;
      });
    }
  };

  const handleReject = async (review: PendingReview) => {
    if (!firebase) return;

    setSubmittingIds(prev => new Set(prev).add(review.id));
    try {
      const success = await firebase.submitReview(
        review.sourceOrgId,
        review.sourceTrackId,
        review.reviewId,
        'rejected',
        {
          rejectionReason: rejectionReason || undefined,
          reviewerName: firebase.auth.currentUser?.displayName || firebase.auth.currentUser?.email || currentOrgName,
          reviewerEmail: firebase.auth.currentUser?.email || null,
          reviewedVia: 'in-app',
        }
      );

      if (success) {
        setPendingReviews(prev => {
          const updated = prev.filter(r => r.id !== review.id);
          setPendingReviewsCount(updated.length);
          return updated;
        });
        setRejectingId(null);
        setRejectionReason('');
      }
    } catch (error) {
      console.error('Error rejecting review:', error);
    } finally {
      setSubmittingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(review.id);
        return newSet;
      });
    }
  };

  const totalCount = pendingReviews.length;

  return (
    <Modal
      isOpen={isOpen}
      toggle={() => setIsOpen(false)}
      style={{
        maxWidth: '650px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={() => setIsOpen(false)}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
        }}
        close={
          <button
            className="close"
            onClick={() => setIsOpen(false)}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>
          Spots for Review {totalCount > 0 ? `(${totalCount})` : ''}
        </span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        {loading ? (
          <div className="text-center py-4">
            <Spinner style={{ color: 'var(--accent-1-color)' }} />
          </div>
        ) : pendingReviews.length === 0 ? (
          <p style={{ color: 'rgba(255, 255, 255, 0.5)', textAlign: 'center', margin: '2rem 0' }}>
            No spots pending review.
          </p>
        ) : (
          pendingReviews.map((review) => {
            const playbackUrl = getPlaybackUrl(review);
            const isSubmitting = submittingIds.has(review.id);
            const isRejecting = rejectingId === review.id;
            const isActive = activeReviewId === review.id;

            return (
              <div
                key={review.id}
                onClick={() => setActiveReviewId(review.id)}
                style={{
                  marginBottom: '1rem',
                  padding: '1rem',
                  backgroundColor: isActive ? 'rgba(255, 255, 255, 0.08)' : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: '8px',
                  border: isActive ? '1px solid var(--accent-1-color)' : '1px solid rgba(255, 255, 255, 0.1)',
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, background-color 0.15s',
                }}
              >
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div>
                    <h6 style={{ color: 'var(--accent-1-color)', margin: 0 }}>
                      {review.trackName}
                    </h6>
                    <small style={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                      From: {review.sourceOrgName}
                    </small>
                  </div>
                </div>

                {/* Metadata pills */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.375rem', marginBottom: '0.75rem' }}>
                  {review.language && review.language.length > 0 && (
                    <span style={{
                      padding: '1px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}>
                      {review.language.map(l => l.label).join(', ')}
                    </span>
                  )}
                  {review.client && (
                    <span style={{
                      padding: '1px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}>
                      {review.client.label}
                    </span>
                  )}
                  {review.agency && (
                    <span style={{
                      padding: '1px 8px',
                      fontSize: '0.75rem',
                      borderRadius: '10px',
                      backgroundColor: 'rgba(255, 255, 255, 0.1)',
                      color: 'rgba(255, 255, 255, 0.7)',
                    }}>
                      {review.agency.label}
                    </span>
                  )}
                </div>

                {/* Spot description — populated from the chase-list brief via
                    audiometa.notes. Shown ABOVE the audio so the reviewer
                    reads the spec first. */}
                {review.notes && (
                  <div style={{
                    marginBottom: '0.75rem',
                    padding: '0.65rem 0.8rem',
                    backgroundColor: 'rgba(255, 180, 50, 0.06)',
                    border: '1px solid rgba(255, 180, 50, 0.2)',
                    borderRadius: '6px',
                  }}>
                    <div style={{ color: 'rgba(255, 180, 50, 0.85)', fontSize: '0.72rem', fontWeight: 500, marginBottom: '0.2rem' }}>
                      Spot description
                    </div>
                    <div style={{ color: '#fff', fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>{review.notes}</div>
                  </div>
                )}

                {/* Stations this spot flights on. Only populated when the
                    review was initiated from a campaign (batch path). */}
                {Array.isArray(review.stations) && review.stations.length > 0 && (
                  <div style={{
                    marginBottom: '0.75rem',
                    padding: '0.65rem 0.8rem',
                    backgroundColor: 'rgba(107, 107, 255, 0.06)',
                    border: '1px solid rgba(107, 107, 255, 0.2)',
                    borderRadius: '6px',
                  }}>
                    <div style={{ color: 'rgba(107, 107, 255, 0.85)', fontSize: '0.72rem', fontWeight: 500, marginBottom: '0.35rem' }}>
                      Flighting on {review.stations.length} station{review.stations.length === 1 ? '' : 's'}
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
                      {review.stations.map((s, i) => (
                        <span
                          key={`${s.flightingCode || s.station}-${i}`}
                          title={s.flightingCode ? `${s.station}${s.mediaOwner ? ` — ${s.mediaOwner}` : ''} · ${s.flightingCode}` : s.station}
                          style={{
                            padding: '2px 8px',
                            borderRadius: '3px',
                            fontSize: '0.72rem',
                            backgroundColor: 'rgba(255,255,255,0.06)',
                            color: '#fff',
                          }}
                        >
                          {s.station || '(unknown)'}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Audio playback */}
                {playbackUrl && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <WavePlayer
                      id={`review-modal-${review.id}`}
                      file={playbackUrl}
                      isActiveTrack={isActive}
                      noAutoplay={!hasStartedPlayback}
                    />
                  </div>
                )}

                {/* Rejection reason input */}
                {isRejecting && (
                  <div style={{ marginBottom: '0.75rem' }}>
                    <Label style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}>
                      Reason for rejection
                    </Label>
                    <Input
                      type="textarea"
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder="Please provide a reason..."
                      rows={2}
                      style={{
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        border: '1px solid rgba(255, 255, 255, 0.2)',
                        color: '#fff',
                        borderRadius: '4px',
                        resize: 'vertical',
                        fontSize: '0.85rem',
                      }}
                    />
                  </div>
                )}

                {/* Action buttons */}
                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  {isRejecting ? (
                    <>
                      <Button
                        size="sm"
                        className="btn-danger-custom"
                        onClick={() => handleReject(review)}
                        disabled={isSubmitting}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>
                          {isSubmitting ? 'Rejecting...' : 'Confirm Rejection'}
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        className="btn-primary-custom"
                        onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                        disabled={isSubmitting}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
                      </Button>
                    </>
                  ) : (
                    <>
                      <Button
                        size="sm"
                        className="btn-success-custom"
                        onClick={() => handleApprove(review)}
                        disabled={isSubmitting}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>
                          {isSubmitting ? 'Approving...' : 'Approve'}
                        </span>
                      </Button>
                      <Button
                        size="sm"
                        className="btn-danger-custom"
                        onClick={() => setRejectingId(review.id)}
                        disabled={isSubmitting}
                      >
                        <span style={{ position: 'relative', zIndex: 2 }}>Reject</span>
                      </Button>
                    </>
                  )}
                </div>
              </div>
            );
          })
        )}
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          justifyContent: 'flex-end',
          padding: '1rem 1.5rem',
        }}
      >
        <Button className="btn-info-custom" onClick={() => setIsOpen(false)}>
          <span style={{ position: 'relative', zIndex: 2 }}>Close</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default PendingReviewsModal;
